// store.js
import React, { createContext, useReducer, useContext } from 'react';
import * as R from 'ramda';

const initialState = {
	error: {
		show: false,
		e: ''
	},
	auth: { session: null },
	widget: { isChildRouteSynced: false },
	payment: {
		retry: 0,
		processing: false
	},
	fullScreenDialog: {
		open: false,
		dlgCmp: null,
		sx: {},
		options: {}
	},
	agreements: {
		sign: false
	}
};
const store = createContext(initialState);
const useStore = () => useContext(store);
const { Provider, Consumer } = store;

const updateState = (state, section, fn) => ({
	...state,
	[section]: R.mergeDeepRight(
		state[section],
		R.is(Function, fn) ? fn(state[section]) : fn
	)
});

const StateProvider = ({ children }) => {
	const [state, dispatch] = useReducer((state, action) => {
		switch (action.type) {
			case 'set session':
				return updateState(state, 'auth', {
					session: action.session
				});
			case 'post error':
				return updateState(state, 'error', {
					show: true,
					e: action.e,
					severity: 'error'
				});
			case 'post success':
				return updateState(state, 'error', {
					show: true,
					e: action.message,
					severity: 'success'
				});
			case 'hide error':
				return updateState(state, 'error', {
					show: false,
					e: {},
					severity: ''
				});

			case 'start processing payment':
				return updateState(state, 'payment', { processing: true });

			case 'stop processing payment':
				return updateState(state, 'payment', { processing: false });

			case 'payment retry increment':
				return updateState(state, 'payment', ({ retry }) => ({
					retry: retry + 1
				}));

			case 'payment retry reset':
				return updateState(state, 'payment', () => ({ retry: 0 }));

			case 'set widget param':
				return updateState(state, 'widget', {
					[action.param]: action.value
				});

			case 'open dialog':
				return updateState(state, 'fullScreenDialog', () => ({
					open: true,
					title: action.title,
					dlgCmp: action.dlgCmp,
					sx: action.sx,
					options: action.options,
					fullScreen: action.fullScreen
				}));

			case 'close dialog':
				return updateState(state, 'fullScreenDialog', () => ({
					title: '',
					open: false,
					dlgCmp: null,
					sx: {},
					options: {},
					fullScreen: true
				}));
			case 'set agreement status':
				return updateState(state, 'agreements', () => ({
					sign: action.sign
				}));

			default:
				throw new Error('Unknown store action');
		}
	}, initialState);

	return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

const withStore = propArr => Cmp => props =>
	(
		<Consumer>
			{value => {
				return <Cmp {...props} {...R.pick(propArr, value)} />;
			}}
		</Consumer>
	);

export { store, StateProvider, withStore, useStore };
